import { PriceUI } from "./PriceUI";
import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import type {
  Display,
  Price,
  PriceRange,
  ReductionHistory,
  ReductionHistoryRange,
} from "@/types/prodcatBaseTypes";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { isM2MRomanBlindOrCurtain } from "@/utils/product/isMadeToMeasure";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant";
import { getPromoMessaging } from "@/utils/product/getPromoMessaging";
import { getPromotionalMessagesToShow } from "@/utils/product/getPromotionalMessagesToShow.ts";
import { isDiorBrand } from "@JohnLewisPartnership/jl-design-system/dist/utils/brands/brands";

type VariantGroupPriceRange = {
  __typename?: "PriceRange";
  display: Display;
  reductionHistory: Array<ReductionHistoryRange>;
};

function getPriceValueOrRange(
  priceData: { max: string; min: string } | string | undefined,
) {
  if (typeof priceData === "object") {
    return priceData.max === priceData.min
      ? priceData.min
      : {
          from: priceData.min,
          to: priceData.max,
        };
  }
  return priceData || "";
}

function getReductionHistoryItem(
  reductionHistory: Array<ReductionHistoryRange | ReductionHistory>,
  chronology: number,
) {
  const historyItem = reductionHistory?.find(
    (item) => item.chronology === chronology,
  );
  return getPriceValueOrRange(historyItem?.display);
}

function convertVariantPriceRangeToFormattedPrice(
  variantPriceRange: PriceRange | Price | VariantGroupPriceRange | undefined,
) {
  return variantPriceRange
    ? {
        was: getReductionHistoryItem(variantPriceRange.reductionHistory, 0),
        then1: getReductionHistoryItem(variantPriceRange.reductionHistory, 1),
        then2: getReductionHistoryItem(variantPriceRange.reductionHistory, 2),
        now: getPriceValueOrRange(variantPriceRange.display),
      }
    : undefined;
}

export function Price({
  product,
  configProps,
  flags,
}: Pick<StandardProps, "product" | "configProps" | "flags">) {
  const { variantPriceRange } = product;
  const { variant, isSelected } = useVariant(product);

  const variantGroupsDefault = Boolean(configProps?.useVariantGroups ?? true);
  const isResponsiveStylesEnabled =
    flags.isResponsiveStylesEnabled.value === true;
  const redesignedPromoPrices = flags.redesignedPromoPrices.value === true;
  const highlightPriceMatchNonPriceEstablished =
    flags.highlightPriceMatchNonPriceEstablished.value === true;

  let priceRange: PriceRange | Price | VariantGroupPriceRange | undefined;

  if (variantGroupsDefault && !isSelected) {
    const variantGroup = getColourGroupForVariant(
      product.variantGroups,
      variant.id,
    );
    const variantGroupPriceRange = variantGroup?.variantPriceRange;

    priceRange = variantGroupPriceRange || variantPriceRange;
  } else {
    priceRange = isSelected ? variant.price : variantPriceRange;
  }

  const brand = product.brand?.name || "";
  const formattedPrice = convertVariantPriceRangeToFormattedPrice(priceRange);

  const priceSuffix =
    isPerMetre(product) || isM2MRomanBlindOrCurtain(product)
      ? "per metre"
      : undefined;
  let isPricePromiseWithHistory = false;
  let isPriceMatchNonPriceEstablished = false;

  if (redesignedPromoPrices || highlightPriceMatchNonPriceEstablished) {
    const { pageMessages } = getPromoMessaging(
      getPromotionalMessagesToShow(
        product,
        variant,
        isSelected,
        variantGroupsDefault && product.variantGroups
          ? getColourGroupForVariant(product.variantGroups, variant.id)
          : undefined,
      ),
    );

    isPricePromiseWithHistory =
      redesignedPromoPrices &&
      !!formattedPrice?.was &&
      pageMessages[0]?.promotionType === "Price match price established";

    isPriceMatchNonPriceEstablished =
      highlightPriceMatchNonPriceEstablished &&
      !isDiorBrand(product.brand?.name) &&
      pageMessages.find(
        (pm) =>
          pm.promotionType === "Price match non price established" &&
          !pm.title.includes("on selected"),
      ) !== undefined;
  }

  return (
    <PriceUI
      price={formattedPrice}
      priceSuffix={priceSuffix}
      brand={brand}
      isResponsiveStylesEnabled={isResponsiveStylesEnabled}
      redesignedPromoPrices={redesignedPromoPrices}
      isPricePromiseWithHistory={isPricePromiseWithHistory}
      highlightPriceMatchNonPriceEstablished={isPriceMatchNonPriceEstablished}
    />
  );
}
